import axiosInstance from '../../axiosInstance';

const BASE = '/api/hcp/profile';

/** For React Query / SWR keyed caches (optional). */
export const HCP_PROFILE_ME_QUERY_KEY = ['hcp-profile', 'me'];

/**
 * Loads HCP maintenance bundle from GET /api/hcp/profile/me (`{ success, data }`).
 * @param {import("axios").AxiosRequestConfig} [extra] — merged after cache-busting defaults.
 */
export async function getHcpProfileMe(extra = {}) {
  const { headers, params, ...rest } = extra;
  const res = await axiosInstance.get(`${BASE}/me`, {
    withCredentials: true,
    ...rest,
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      ...(headers || {}),
    },
    params: {
      _: Date.now(),
      ...(params || {}),
    },
  });
  const body = res.data;
  if (body && body.success === true && body.data != null) {
    return { success: true, data: body.data };
  }
  const msg =
    (body && (body.message || body.error)) || 'Could not load HCP profile';
  return {
    success: false,
    message: typeof msg === 'string' ? msg : 'Could not load HCP profile',
  };
}

/** Call after mutations if you add TanStack Query keyed by {@link HCP_PROFILE_ME_QUERY_KEY}. */
export function invalidateHcpProfileMeClientCache() {
  /* e.g. queryClient.invalidateQueries({ queryKey: HCP_PROFILE_ME_QUERY_KEY }) */
}

/**
 * @param {number} [limit]
 * @param {import("axios").AxiosRequestConfig} [extra]
 */
export async function getHcpProfileAudit(limit = 50, extra = {}) {
  const { headers, params, ...rest } = extra;
  const res = await axiosInstance.get(`${BASE}/me/audit`, {
    withCredentials: true,
    ...rest,
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      ...(headers || {}),
    },
    params: {
      limit,
      _: Date.now(),
      ...(params || {}),
    },
  });
  return res.data;
}

/**
 * @param {object} values — unlocked general fields (camelCase)
 * @param {File|null} idImageFile
 */
export async function patchHcpProfileGeneral(values, idImageFile) {
  const fd = new FormData();
  const keys = [
    'firstName',
    'lastName',
    'nationality',
    'countryCode',
    'phoneNumber',
    'gender',
    'addressCountry',
    'addressState',
    'addressCity',
    'addressStreet',
    'addressZipCode',
  ];
  keys.forEach((k) => {
    if (values[k] != null) fd.append(k, values[k]);
  });
  if (idImageFile instanceof File) fd.append('id_image', idImageFile);
  const res = await axiosInstance.patch(`${BASE}/me/general`, fd, {
    withCredentials: true,
  });
  return res.data;
}

/**
 * Full general segment update; server enforces locked fields via draft validation snapshot.
 * @param {object} values — camelCase general fields (including email/DOB/ID when editable)
 * @param {File|null} idImageFile
 */
export async function putHcpProfileGeneralInfo(values, idImageFile) {
  const fd = new FormData();
  const keys = [
    'firstName',
    'lastName',
    'email',
    'dateOfBirth',
    'idNumber',
    'nationality',
    'countryCode',
    'phoneNumber',
    'gender',
    'addressCountry',
    'addressState',
    'addressCity',
    'addressStreet',
    'addressZipCode',
  ];
  keys.forEach((k) => {
    const v = values[k];
    if (v === undefined || v === null) return;
    fd.append(k, v);
  });
  if (idImageFile instanceof File) fd.append('id_image', idImageFile);
  const res = await axiosInstance.put(`${BASE}/general-info`, fd, {
    withCredentials: true,
  });
  return res.data;
}

/**
 * Mirrors signup license payload (`hcpSignupApi.postLicensesDraft`) but profile endpoint.
 * @param {number} draftId
 * @param {object[]} licenseRows
 * @param {object} licenseFieldConfig
 */
export async function patchHcpProfileLicenses(
  draftId,
  licenseRows,
  licenseFieldConfig
) {
  const fd = new FormData();
  fd.append('draftId', String(draftId));

  const payload = licenseRows.map((r) => ({
    prefix: r.prefix || '',
    licenseNumber: r.licenseNumber || '',
    issuingCountry: r.issuingCountry || '',
    authorityName: r.authorityName || '',
    issueDate: r.issueDate || '',
    expiryDate: r.expiryDate || '',
    existingAttachmentUploadId: r.existingAttachmentUploadId ?? null,
  }));

  fd.append('licenses', JSON.stringify(payload));
  fd.append(
    'licenseConfig',
    JSON.stringify(
      licenseFieldConfig && typeof licenseFieldConfig === 'object'
        ? licenseFieldConfig
        : {}
    )
  );

  licenseRows.forEach((row, i) => {
    if (row.attachmentFile instanceof File)
      fd.append(`attachment_${i}`, row.attachmentFile);
  });

  const res = await axiosInstance.patch(`${BASE}/me/licenses`, fd, {
    withCredentials: true,
  });
  return res.data;
}

export async function getHcpProfileLicenses() {
  const res = await axiosInstance.get(`${BASE}/licenses`, {
    withCredentials: true,
  });
  return res.data;
}

/**
 * @param {Record<string, string|number>} fields — camelCase license fields
 * @param {File|null} attachmentFile — required on create
 */
export async function postHcpProfileLicense(fields, attachmentFile) {
  const fd = new FormData();
  [
    'prefix',
    'licenseNumber',
    'issuingCountry',
    'authorityName',
    'issueDate',
    'expiryDate',
  ].forEach((k) => {
    const v = fields[k];
    if (v !== undefined && v !== null) fd.append(k, String(v));
  });
  if (attachmentFile instanceof File) fd.append('attachment', attachmentFile);
  const res = await axiosInstance.post(`${BASE}/licenses`, fd, {
    withCredentials: true,
  });
  return res.data;
}

/**
 * @param {number} licenseRowId
 * @param {Record<string, string|number>} fields
 * @param {File|null} attachmentFile — optional replacement
 */
export async function putHcpProfileLicense(
  licenseRowId,
  fields,
  attachmentFile
) {
  const fd = new FormData();
  [
    'prefix',
    'licenseNumber',
    'issuingCountry',
    'authorityName',
    'issueDate',
    'expiryDate',
  ].forEach((k) => {
    const v = fields[k];
    if (v !== undefined && v !== null) fd.append(k, String(v));
  });
  if (attachmentFile instanceof File) fd.append('attachment', attachmentFile);
  const res = await axiosInstance.put(`${BASE}/licenses/${licenseRowId}`, fd, {
    withCredentials: true,
  });
  return res.data;
}

export async function deleteHcpProfileLicense(licenseRowId) {
  const res = await axiosInstance.delete(`${BASE}/licenses/${licenseRowId}`, {
    withCredentials: true,
  });
  return res.data;
}

/**
 * Mirrors `hcpSignupApi.postEducationExperienceDraft` shape.
 */
export async function patchHcpProfileEducation(draftId, values) {
  const fd = new FormData();
  fd.append('draftId', String(draftId));

  const educationsPayload = (values.educations || []).map((r) => ({
    countryOfCertificate: r.countryOfCertificate ?? '',
    degreeName: r.degreeName ?? '',
    institutionName: r.institutionName ?? '',
    authorityName: r.authorityName ?? '',
    fieldOfStudy: r.fieldOfStudy ?? '',
    graduationDate: r.graduationDate ?? '',
    studyYears:
      typeof r.studyYears === 'number' && Number.isFinite(r.studyYears)
        ? r.studyYears
        : Number(r.studyYears),
    accreditationStatus: r.accreditationStatus ?? '',
    accreditingAuthority: r.accreditingAuthority ?? '',
    existingAccreditationDocumentUploadId:
      r.existingAccreditationDocumentUploadId ?? null,
  }));

  fd.append('educations', JSON.stringify(educationsPayload));

  const ex = values.experience || {};
  fd.append(
    'experience',
    JSON.stringify({
      internshipTraining: ex.internshipTraining ?? '',
      postGraduationInternship: ex.postGraduationInternship ?? '',
      internshipCompletion: ex.internshipCompletion ?? '',
      internshipStart: ex.internshipStart ?? '',
      internshipEnd: ex.internshipEnd ?? '',
      existingInternshipCertificateUploadId:
        ex.existingInternshipCertificateUploadId ?? null,
      additionalExperience: ex.additionalExperience ?? '',
      additionalExperienceStart: ex.additionalExperienceStart ?? '',
      additionalExperienceEnd: ex.additionalExperienceEnd ?? '',
      existingAdditionalExperienceCertificateUploadId:
        ex.existingAdditionalExperienceCertificateUploadId ?? null,
      undergraduateInternship: ex.undergraduateInternship ?? '',
      undergraduateInternshipStart: ex.undergraduateInternshipStart ?? '',
      undergraduateInternshipEnd: ex.undergraduateInternshipEnd ?? '',
      existingUndergraduateInternshipCertificateUploadId:
        ex.existingUndergraduateInternshipCertificateUploadId ?? null,
      existingRelevantAttachmentIds: Array.isArray(
        ex.existingRelevantAttachmentIds
      )
        ? ex.existingRelevantAttachmentIds
        : [],
    })
  );

  (values.educations || []).forEach((row, i) => {
    if (row.accreditationFile instanceof File)
      fd.append(`accreditation_doc_${i}`, row.accreditationFile);
  });

  if (ex.internshipCertificateFile instanceof File) {
    fd.append('internship_certificate', ex.internshipCertificateFile);
  }
  if (ex.additionalExperienceCertificateFile instanceof File) {
    fd.append(
      'additional_experience_certificate',
      ex.additionalExperienceCertificateFile
    );
  }
  if (ex.undergraduateInternshipCertificateFile instanceof File) {
    fd.append(
      'undergraduate_internship_certificate',
      ex.undergraduateInternshipCertificateFile
    );
  }

  (ex.relevantAttachmentFiles || []).forEach((file, j) => {
    if (file instanceof File) fd.append(`experience_attachment_${j}`, file);
  });

  const res = await axiosInstance.patch(`${BASE}/me/education-experience`, fd, {
    withCredentials: true,
  });
  return res.data;
}

export async function getHcpProfileEducation() {
  const res = await axiosInstance.get(`${BASE}/education`, {
    withCredentials: true,
  });
  return res.data;
}

const EDU_BODY_KEYS = [
  'countryOfCertificate',
  'degreeName',
  'institutionName',
  'authorityName',
  'fieldOfStudy',
  'graduationDate',
  'studyYears',
  'accreditationStatus',
  'accreditingAuthority',
  'existingAccreditationDocumentUploadId',
];

export async function postHcpProfileEducation(fields, accreditationFile) {
  const fd = new FormData();
  EDU_BODY_KEYS.forEach((k) => {
    const v = fields[k];
    if (v !== undefined && v !== null) fd.append(k, String(v));
  });
  if (accreditationFile instanceof File)
    fd.append('accreditation_document', accreditationFile);
  const res = await axiosInstance.post(`${BASE}/education`, fd, {
    withCredentials: true,
  });
  return res.data;
}

export async function putHcpProfileEducation(
  educationRowId,
  fields,
  accreditationFile
) {
  const fd = new FormData();
  EDU_BODY_KEYS.forEach((k) => {
    const v = fields[k];
    if (v !== undefined && v !== null) fd.append(k, String(v));
  });
  if (accreditationFile instanceof File)
    fd.append('accreditation_document', accreditationFile);
  const res = await axiosInstance.put(
    `${BASE}/education/${educationRowId}`,
    fd,
    {
      withCredentials: true,
    }
  );
  return res.data;
}

export async function deleteHcpProfileEducation(educationRowId) {
  const res = await axiosInstance.delete(
    `${BASE}/education/${educationRowId}`,
    {
      withCredentials: true,
    }
  );
  return res.data;
}

/**
 * @param {object} exp — experience JSON fields (camelCase)
 * @param {File|null} internshipCertificateFile
 * @param {File[]} [relevantAttachmentFiles] — appended as experience_attachment_0…
 * @param {File|null} [additionalExperienceCertificateFile]
 * @param {File|null} [undergraduateInternshipCertificateFile]
 */
export async function putHcpProfileExperience(
  exp,
  internshipCertificateFile,
  relevantAttachmentFiles,
  additionalExperienceCertificateFile,
  undergraduateInternshipCertificateFile
) {
  const fd = new FormData();
  fd.append(
    'experience',
    JSON.stringify({
      internshipTraining: exp.internshipTraining ?? '',
      postGraduationInternship: exp.postGraduationInternship ?? '',
      internshipCompletion: exp.internshipCompletion ?? '',
      internshipStart: exp.internshipStart ?? '',
      internshipEnd: exp.internshipEnd ?? '',
      existingInternshipCertificateUploadId:
        exp.existingInternshipCertificateUploadId ?? null,
      additionalExperience: exp.additionalExperience ?? '',
      additionalExperienceStart: exp.additionalExperienceStart ?? '',
      additionalExperienceEnd: exp.additionalExperienceEnd ?? '',
      existingAdditionalExperienceCertificateUploadId:
        exp.existingAdditionalExperienceCertificateUploadId ?? null,
      undergraduateInternship: exp.undergraduateInternship ?? '',
      undergraduateInternshipStart: exp.undergraduateInternshipStart ?? '',
      undergraduateInternshipEnd: exp.undergraduateInternshipEnd ?? '',
      existingUndergraduateInternshipCertificateUploadId:
        exp.existingUndergraduateInternshipCertificateUploadId ?? null,
      existingRelevantAttachmentIds: Array.isArray(
        exp.existingRelevantAttachmentIds
      )
        ? exp.existingRelevantAttachmentIds
        : [],
    })
  );
  if (internshipCertificateFile instanceof File) {
    fd.append('internship_certificate', internshipCertificateFile);
  }
  if (additionalExperienceCertificateFile instanceof File) {
    fd.append(
      'additional_experience_certificate',
      additionalExperienceCertificateFile
    );
  }
  if (undergraduateInternshipCertificateFile instanceof File) {
    fd.append(
      'undergraduate_internship_certificate',
      undergraduateInternshipCertificateFile
    );
  }
  (relevantAttachmentFiles || []).forEach((file, j) => {
    if (file instanceof File) fd.append(`experience_attachment_${j}`, file);
  });
  const res = await axiosInstance.put(`${BASE}/experience`, fd, {
    withCredentials: true,
  });
  return res.data;
}
